/**
 * Publishing Schedule Calculation Utility
 *
 * Calculates the next publish date for stories based on show-specific weekday configuration.
 * Supports flexible multi-day scheduling (e.g., Mon/Wed/Fri) with per-show overrides.
 *
 * @module publishScheduling
 */

import type { Weekday } from './types/config.ts';

/**
 * Map weekday names to JavaScript Date day numbers (0 = Sunday, 6 = Saturday)
 */
const WEEKDAY_MAP: Record<Weekday, number> = {
  sun: 0,
  mon: 1,
  tue: 2,
  wed: 3,
  thu: 4,
  fri: 5,
  sat: 6,
};

/**
 * Parse comma-separated weekdays string into array of Weekday values.
 *
 * Tokens are lowercased and validated against the known weekday names.
 * Invalid tokens (e.g., "monday", typos) are dropped rather than cast blindly —
 * an all-invalid string yields [], which callers treat as "no weekdays configured".
 *
 * @param weekdaysString - Comma-separated weekdays (e.g., "mon,wed,fri")
 * @returns Array of valid Weekday values
 *
 * @example
 * parseWeekdays("mon,wed,fri") // Returns: ["mon", "wed", "fri"]
 * parseWeekdays("Mon, WED") // Returns: ["mon", "wed"]
 * parseWeekdays("monday,funday") // Returns: []
 * parseWeekdays("") // Returns: []
 */
export function parseWeekdays(weekdaysString: string): Weekday[] {
  if (!weekdaysString || weekdaysString.trim() === '') {
    return [];
  }
  const validWeekdays = new Set(Object.keys(WEEKDAY_MAP));
  return weekdaysString
    .split(',')
    .map(day => day.trim().toLowerCase())
    .filter((day): day is Weekday => validWeekdays.has(day));
}

/**
 * Find the next occurrence of any weekday from a list of target weekdays.
 * Includes the baseline day if it matches a target weekday.
 *
 * @param baseline - The starting date to calculate from
 * @param targetWeekdays - Array of target weekdays to match
 * @returns Date object set to the next occurrence of any target weekday
 *
 * @example
 * // If baseline is Sunday and targets are [mon, wed, fri]
 * // Returns next Monday (1 day later)
 *
 * // If baseline is Monday and targets are [mon, wed, fri]
 * // Returns Monday (0 days - same day included)
 */
export function getNextMatchingWeekday(baseline: Date, targetWeekdays: Weekday[]): Date {
  if (targetWeekdays.length === 0) {
    throw new Error('targetWeekdays cannot be empty');
  }

  // Convert target weekdays to day numbers
  const targetDays = targetWeekdays.map(wd => WEEKDAY_MAP[wd]);
  const result = new Date(baseline);
  const currentDay = result.getUTCDay();

  // Find the closest future occurrence
  let minDaysToAdd = 8; // More than a week
  for (const targetDay of targetDays) {
    let daysToAdd = targetDay - currentDay;
    if (daysToAdd < 0) {
      daysToAdd += 7; // Move to next week
    }
    if (daysToAdd < minDaysToAdd) {
      minDaysToAdd = daysToAdd;
    }
  }

  result.setUTCDate(result.getUTCDate() + minDaysToAdd);
  return result;
}

/**
 * Parameters for pure publish date calculation (no database access)
 */
export interface PublishSchedulingParams {
  storyId: string;
  showSlug: string;
  customPublishWeekdays: string | null;
  defaultPublishWeekdays: string; // Comma-separated weekdays (e.g., "tue" or "mon,wed,fri")
  publishHour: number;
  latestPublishDate: string | null;
  todayStory: { id: string; published_at: string } | null;
}

/**
 * Calculate the next publish date for a story (PURE FUNCTION - no database access)
 *
 * Algorithm:
 * 1. Determine effective weekdays (show custom or config default)
 * 2. Calculate baseline date:
 *    - If today is scheduled AND no story exists for today → use today (BACKFILL)
 *    - If published stories exist: use max(day after last publish, start of today)
 *    - If no published stories: use start of today
 * 3. Find next occurrence of any target weekday from baseline
 * 4. Set time to configured publish hour (UTC)
 * 5. Return ISO 8601 timestamp
 *
 * @param params - All data required for calculation (no DB access)
 * @returns ISO 8601 timestamp string for the next publish date
 *
 * @example
 * const nextDate = calculateNextPublishDate({
 *   storyId: 'abc123',
 *   showSlug: 'my-show',
 *   customPublishWeekdays: 'mon,wed,fri',
 *   defaultPublishWeekdays: 'mon',
 *   publishHour: 11,
 *   latestPublishDate: '2025-12-16T11:00:00.000Z',
 *   todayStory: null
 * });
 * // Returns: "2025-12-18T11:00:00.000Z"
 */
export function calculateNextPublishDate(
  params: PublishSchedulingParams
): string {
  const {
    showSlug,
    customPublishWeekdays,
    defaultPublishWeekdays,
    publishHour,
    latestPublishDate,
    todayStory
  } = params;

  // 1. Determine effective weekdays (show custom or config default)
  const weekdaysString = customPublishWeekdays || defaultPublishWeekdays;
  const weekdays = parseWeekdays(weekdaysString);

  if (weekdays.length === 0) {
    throw new Error(`No weekdays configured for show: ${showSlug}`);
  }

  // 2. Calculate baseline date for scheduling
  // Start of today (00:00 UTC) - used as minimum baseline
  const todayStart = new Date();
  todayStart.setUTCHours(0, 0, 0, 0);

  // Check if today is a scheduled weekday
  const currentWeekday = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'][todayStart.getUTCDay()] as Weekday;
  const isTodayScheduled = weekdays.includes(currentWeekday);

  let baseline: Date;

  // BACKFILL LOGIC: If today is scheduled AND no story exists for today, schedule for today
  if (isTodayScheduled && !todayStory) {
    baseline = todayStart;
  } else if (latestPublishDate) {
    // Normal logic: Get start of day AFTER the last published date
    // Example: If story published Wed 10:00 or Wed 23:00, dateEnd = Thu 00:00
    const lastPublished = new Date(latestPublishDate);
    const dateEnd = new Date(lastPublished);
    dateEnd.setUTCHours(0, 0, 0, 0); // Start of publish day
    dateEnd.setUTCDate(dateEnd.getUTCDate() + 1); // Move to next day

    // Use the later of: (day after last publish) or (today)
    // This ensures:
    // - If last publish was today → baseline is tomorrow (no same-day duplicate)
    // - If last publish was yesterday or earlier → baseline is today (today is eligible)
    baseline = new Date(Math.max(dateEnd.getTime(), todayStart.getTime()));
  } else {
    // No published stories yet - start from today
    baseline = todayStart;
  }

  // 3. Find next occurrence of any target weekday from baseline
  const nextDate = getNextMatchingWeekday(baseline, weekdays);

  // 4. Set time to configured publish hour (UTC)
  nextDate.setUTCHours(publishHour, 0, 0, 0);

  // 5. Return ISO 8601 string
  return nextDate.toISOString();
}
